// src/pages/Telephony/Softphone.tsx
//
// Full softphone screen: connection settings, dial pad, custom headers and the
// call log.
//
// The SIP session itself does NOT live here — it lives in
// telephony/softphoneStore, outside React, so the registration survives the
// agent navigating away. This page is one view onto it; the floating
// SoftphoneWidget is another, and both show the same call.
import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Phone, PhoneOff, PhoneCall, Settings, Wifi, WifiOff, Delete, ShieldAlert, Forward } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { useSoftphoneEntitlement } from 'hooks/useSoftphoneEntitlement';
import axiosInstance from 'api/axios.config';
import { API_ENDPOINTS } from 'api/endpoints';
import {
  subscribe, getState, actions, PRESETS, isMixedContent, Cfg,
} from 'telephony/softphoneStore';

// Live-call tooling is restricted to the highest-trust roles — it places real
// outbound calls and bridges live audio. Keep this in sync with the Sidebar
// entry roles for /telephony/softphone and with SoftphoneWidget.
const ALLOWED_ROLES = ['master_admin', 'super_admin'];

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

// A provider topology the softphone can place a test call to (from the backend,
// resolved from the providers DB). Dialing `dialTarget` routes through that
// provider's path (direct/kamailio × asterisk/freeswitch) via the [webphone]
// dialplan's func_odbc lookup.
type TestTarget = {
  providerId: number; name: string; engineType: string; routingMode: string;
  topology: string; sipEndpoint?: string; dialTarget: string;
};

const Softphone: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const role = (currentOrganization?.role || user?.role || '').toLowerCase();

  // Hand-entered SIP credentials are a platform diagnostic: the presets point at
  // test endpoints and a raw WSS URL can aim a phone at any switch, in any
  // tenant's context. Everyone else gets the phone their organization's provider
  // issues, which is the only one scoped to them anyway.
  const canUseManual = role === 'master_admin';
  const roleAllowed = ALLOWED_ROLES.includes(role);
  // Purchased seats only (2026-10-14). The server refuses the credential
  // anyway; asking first lets this page explain instead of provisioning into a
  // 403. Undefined while loading — nothing is fetched until it is known.
  const entitlement = useSoftphoneEntitlement();
  const allowed = roleAllowed && entitlement?.allowed === true;

  const state = useSyncExternalStore(subscribe, getState);
  const { cfg, phase, presetId, source, outHeaders, caller, inHeaders, profile, profileState, logLines,
          providers, switchable, providerId, provisionError } = state;

  const [showCfg, setShowCfg] = useState(false);
  const [dest, setDest] = useState('1000');
  const [xfer, setXfer] = useState('');
  const [targets, setTargets] = useState<TestTarget[]>([]);
  const logBoxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { if (logBoxRef.current) logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight; }, [logLines]);

  // The switches this ORGANIZATION may register to. Loaded on mount rather than
  // at connect time so the picker is already drawn when a user with more than
  // one has to choose — the server refuses to guess between them, so without a
  // choice the phone never provisions at all.
  useEffect(() => { if (allowed) actions.loadProviders(); }, [allowed]);

  // Load the provider topologies this softphone can test (from the providers DB).
  useEffect(() => {
    if (!allowed) return;
    (async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.SOFTPHONE_TEST_TARGETS);
        const d = res.data?.data || res.data;
        if (Array.isArray(d)) setTargets(d);
      } catch { /* providers optional — manual dial still works */ }
    })();
  }, [allowed]);

  // NOTE: no teardown on unmount. That is the point — leaving this page must
  // not drop the agent's registration.

  const connect = useCallback(() => {
    // Non-master users have no manual path at all, so `source` is not consulted
    // for them. Reading it would let a stale localStorage value from before this
    // restriction put them back on hand-entered credentials.
    if (!canUseManual) return actions.provisionAndConnect();
    return source === 'server' ? actions.provisionAndConnect() : actions.connect();
  }, [source, canUseManual]);

  const online = phase === 'registered' || phase === 'ringing' || phase === 'calling' || phase === 'in-call';
  const inCall = phase === 'calling' || phase === 'in-call';
  const ringing = phase === 'ringing';
  const mixedContent = isMixedContent(cfg);

  const badge = {
    offline: ['Offline', 'bg-gray-100 text-gray-600'],
    connecting: ['Connecting…', 'bg-amber-100 text-amber-700'],
    registered: ['Registered', 'bg-green-100 text-green-700'],
    ringing: ['Incoming call', 'bg-purple-100 text-purple-700'],
    calling: ['Calling…', 'bg-amber-100 text-amber-700'],
    'in-call': ['In call', 'bg-blue-100 text-blue-700'],
  }[phase];

  if (roleAllowed && !entitlement) {
    return <div className="p-6 max-w-2xl mx-auto text-sm text-gray-500 dark:text-gray-400">Checking softphone access…</div>;
  }

  if (roleAllowed && entitlement && !entitlement.allowed) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center" data-testid="softphone-not-entitled">
          <ShieldAlert className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Softphone not available</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            {entitlement.reason || 'Softphone needs purchased seats — contact Green Giraffe.'}
          </p>
          {entitlement.maxSeats != null && entitlement.maxSeats > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Seats in use: {entitlement.usedSeats} of {entitlement.maxSeats}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <ShieldAlert className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Restricted</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">The Test Softphone places live calls and is available to master admins and super admins only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2"><PhoneCall className="w-6 h-6 text-blue-600" /> Softphone</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Stays registered while you work — the floating widget follows you to every page.
          </p>
        </div>
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full ${badge[1]}`}>{online ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}{badge[0]}</span>
      </div>

      {/* Connection settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <button onClick={() => setShowCfg(s => !s)} className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-200">
          <span className="flex items-center gap-2"><Settings className="w-4 h-4" /> Connection ({
            // Without a manual option the stored cfg is a placeholder the user
            // will never register with — showing "webphone@127.0.0.1" to someone
            // whose phone is issued by the server states something untrue about
            // where their calls come from.
            canUseManual ? `${cfg.user}@${cfg.domain}` : (online ? `${cfg.user}@${cfg.domain}` : 'issued by the platform')
          })</span>
          <span className="text-gray-400">{showCfg ? '▲' : '▼'}</span>
        </button>
        {showCfg && !canUseManual && (
          <div className="px-4 pb-4 text-xs text-gray-500 dark:text-gray-400">
            Your phone is issued by the platform: it registers to the provider your
            organization is connected to, using credentials the server holds. There is
            nothing to enter. The login stays the same between sessions and can be
            reissued if it is ever exposed.
          </div>
        )}
        {/* The switch this phone registers to. Shown only when the organization
            has more than one — with a single switch there is no decision, and
            offering it as a question implies one. */}
        {showCfg && switchable && (
          <div className="px-4 pb-4">
            <label className="text-xs text-gray-500 dark:text-gray-400">
              Switch
              <select
                value={providerId ?? ''}
                onChange={e => actions.selectProvider(e.target.value ? Number(e.target.value) : null)}
                className="mt-1 w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-100">
                <option value="">Choose a switch…</option>
                {providers.map(p => (
                  <option key={p.providerId} value={p.providerId}>
                    {p.name}{p.region ? ` — ${p.region}` : ''}
                  </option>
                ))}
              </select>
              <span className="block mt-1 text-gray-400">
                Your organization is connected to more than one. Changing this
                reconnects the phone.
              </span>
            </label>
          </div>
        )}
        {showCfg && !canUseManual && (
          <div className="px-4 pb-4">
            <button
              onClick={() => actions.reissueCredential()}
              className="text-xs px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
              Reissue credential
            </button>
            <span className="ml-2 text-xs text-gray-400">
              Replaces the SIP password and reconnects. Use if it may have been exposed.
            </span>
          </div>
        )}
        {/* Why the phone is not connected, in words. This used to be a log line
            and a silent fall back to saved settings, so a phone that could not
            be issued looked the same as one nobody had set up. */}
        {provisionError && (
          <div className="mx-4 mb-4 px-3 py-2 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200">
            {provisionError}
          </div>
        )}
        {showCfg && canUseManual && (
          <div className="px-4 pb-4 grid grid-cols-2 gap-3">
            <label className="text-xs text-gray-500 col-span-2">
              Environment
              <select
                value={presetId} disabled={online}
                onChange={e => actions.applyPreset(e.target.value)}
                className="mt-1 w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-100 disabled:opacity-60">
                {PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <span className="block mt-1 text-gray-400">{PRESETS.find(p => p.id === presetId)?.hint}</span>
            </label>
            {mixedContent && (
              <p className="col-span-2 text-xs text-red-600 dark:text-red-400">
                This page is served over https, so the browser will block the insecure <code>ws://</code> URL above. Use a <code>wss://</code> endpoint.
              </p>
            )}
            {([['ws', 'WebSocket URL'], ['domain', 'SIP domain'], ['user', 'Username'], ['pass', 'Password']] as [keyof Cfg, string][]).map(([k, label]) => (
              <label key={k} className="text-xs text-gray-500 col-span-2 sm:col-span-1">
                {label}
                <input
                  type={k === 'pass' ? 'password' : 'text'} value={cfg[k]} disabled={online}
                  onChange={e => actions.setCfg({ [k]: e.target.value } as Partial<Cfg>)}
                  className="mt-1 w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-100 disabled:opacity-60"
                />
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Call context — caller, pre-answer lookup, and any metadata the platform
          attached. Stays up for the WHOLE call: the moment an agent answers is
          exactly when they need the customer in front of them. */}
      {(ringing || phase === 'in-call') && (caller || Object.keys(inHeaders).length > 0) && (
        <div className={`rounded-lg p-4 border flex items-start justify-between gap-3 ${ringing
          ? 'bg-purple-50 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700'
          : 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700'}`}>
          <div className="min-w-0">
            <span className={`flex items-center gap-2 font-medium ${ringing
              ? 'text-purple-800 dark:text-purple-200' : 'text-blue-800 dark:text-blue-200'}`}>
              <PhoneCall className={`w-5 h-5 ${ringing ? 'animate-pulse' : ''}`} />
              {ringing ? 'Incoming call' : 'On call'}
            </span>
            {caller && <div className="mt-1 text-lg font-mono text-gray-900 dark:text-gray-100 truncate">{caller}</div>}

            {/* Every lookup state is shown explicitly: an agent must be able to
                tell "not a known customer" from "the lookup broke". */}
            {profileState === 'loading' && <div className="mt-1 text-xs text-gray-500">looking up caller…</div>}
            {profileState === 'none' && <div className="mt-1 text-xs text-gray-500">not in contacts</div>}
            {profileState === 'error' && <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">lookup unavailable</div>}
            {profileState === 'found' && profile && (
              <dl className="mt-2 text-xs text-gray-800 dark:text-gray-200 space-y-0.5">
                {[
                  ['Name', profile.name || [profile.firstName, profile.lastName].filter(Boolean).join(' ')],
                  ['Company', profile.company || profile.organizationName],
                  ['Email', profile.email],
                  ['Tags', Array.isArray(profile.tags) ? profile.tags.join(', ') : profile.tags],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={String(k)} className="flex gap-2">
                    <dt className="opacity-70">{k}</dt>
                    <dd className="font-medium">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            )}
            {Object.keys(inHeaders).length > 0 && (
              <dl className="mt-2 text-xs text-gray-700 dark:text-gray-300 space-y-0.5">
                {Object.entries(inHeaders).map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <dt className="opacity-70">{k.replace(/^x-/i, '')}</dt>
                    <dd className="font-mono">{v}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
          {ringing && (
            <div className="flex gap-2 shrink-0">
              <button onClick={() => actions.answer()} className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> Answer
              </button>
              <button onClick={() => actions.decline()} className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 inline-flex items-center gap-2">
                <PhoneOff className="w-4 h-4" /> Decline
              </button>
            </div>
          )}
        </div>
      )}

      {/* Connect / dial */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-3">
        <div className="flex gap-2">
          {!online
            ? <button onClick={connect} disabled={phase === 'connecting'} className="tl-btn tl-btn-primary flex-1 disabled:opacity-50"><Wifi className="w-4 h-4" /> Connect &amp; Register</button>
            : <button onClick={() => actions.disconnect()} className="flex-1 px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 inline-flex items-center justify-center gap-2"><WifiOff className="w-4 h-4" /> Disconnect</button>}
        </div>

        {/* Provider topology to test — dialing routes through that provider's
            path (direct/kamailio × asterisk/freeswitch) via the webphone dialplan. */}
        {targets.length > 0 && (
          <label className="block text-xs text-gray-500">
            Test provider
            <select
              value={targets.some(t => t.dialTarget === dest) ? dest : ''}
              onChange={e => { if (e.target.value) setDest(e.target.value); }}
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100">
              <option value="">— pick a provider topology to test —</option>
              {targets.map(t => (
                <option key={t.providerId} value={t.dialTarget}>{t.name} · {t.topology} ({t.dialTarget})</option>
              ))}
            </select>
          </label>
        )}

        <label className="block text-xs text-gray-500">
          Custom SIP headers <span className="opacity-70">— one per line, e.g. <code>X-Campaign-Id: camp-42</code></span>
          <textarea
            value={outHeaders} onChange={e => actions.setOutHeaders(e.target.value)} rows={2}
            placeholder="X-Campaign-Id: camp-42"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 font-mono text-xs" />
        </label>

        <div className="flex gap-2">
          <input value={dest} onChange={e => setDest(e.target.value)} placeholder="number to dial"
            className="flex-1 px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 font-mono" />
          {!inCall
            ? <button onClick={() => actions.call(dest)} disabled={!online} className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 inline-flex items-center gap-2"><Phone className="w-4 h-4" /> Call</button>
            : <button onClick={() => actions.hangup()} className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 inline-flex items-center gap-2"><PhoneOff className="w-4 h-4" /> Hang up</button>}
        </div>

        {/* Blind transfer — hand the far party over and drop out. Only useful
            mid-call, so it is hidden otherwise. */}
        {inCall && (
          <div className="flex gap-2">
            <input value={xfer} onChange={e => setXfer(e.target.value)} placeholder="transfer to (e.g. 100)"
              onKeyDown={e => { if (e.key === 'Enter' && xfer) { actions.transfer(xfer); setXfer(''); } }}
              className="flex-1 px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 font-mono" />
            <button onClick={() => { if (xfer) { actions.transfer(xfer); setXfer(''); } }} disabled={!xfer}
              className="px-5 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-2">
              <Forward className="w-4 h-4" /> Transfer
            </button>
          </div>
        )}

        {/* DTMF keypad */}
        <div className="grid grid-cols-3 gap-2">
          {KEYS.map(k => (
            <button key={k} onClick={() => actions.dtmf(k)} disabled={!inCall}
              className="py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 font-mono hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40">
              {k}
            </button>
          ))}
        </div>
      </div>

      {/* Log */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-medium text-gray-700 dark:text-gray-200">Log</h2>
          <Delete className="w-4 h-4 text-gray-400" />
        </div>
        <div ref={logBoxRef} className="h-56 overflow-y-auto bg-gray-50 dark:bg-gray-900 rounded p-2 font-mono text-xs text-gray-700 dark:text-gray-300 space-y-0.5">
          {logLines.length === 0
            ? <div className="text-gray-400">nothing yet</div>
            : logLines.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      </div>
    </div>
  );
};

export default Softphone;
